/**
 * Shared types + tiny API helpers for the operator Teams UI.
 * Backend contract (all responses are { status, data }):
 *   GET    /api/teams?venueId=<int>
 *   GET    /api/teams/:id
 *   GET    /api/teams/unassociated?venueId=<int>[&seasonId=<int>]
 *   POST   /api/teams                  { name, venueId }
 *   PATCH  /api/teams/:id              { name?, notes? }
 *   POST   /api/teams/:id/assign       { liveTeamIds: [] }
 *   POST   /api/teams/:id/unassign     { liveTeamIds: [] }
 *   POST   /api/teams/:id/merge        { intoId }
 *   DELETE /api/teams/:id
 */
import api from '../../utils/api';

export interface VenueOption {
  id: number;
  name: string;
}

export interface TeamSummary {
  id: number;
  name: string;
  venueId: number;
  notes: string | null;
  visits: number;
  variantCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  totalPlayers: number;
  avgPlayers: number | null;
  avgPlacement: number | null;
  wins: number;
}

export interface TeamListResponse {
  teams: TeamSummary[];
  unassociatedCount: number;
}

export interface TeamCore {
  id: number;
  name: string;
  venueId: number;
  notes: string | null;
}

export interface TeamStats {
  visits: number;
  avgPlayers: number | null;
  avgPlacement: number | null;
  wins: number;
  podiums: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

export interface TeamHistoryRow {
  liveTeamId: number;
  eventId: string;
  date: string;
  quizName: string;
  teamNameUsed: string;
  playerCount: number | null;
  grandTotal: number | null;
  placement: number | null;
  totalTeams: number | null;
  leftEarly: boolean;
}

export interface TeamDetailResponse {
  team: TeamCore;
  stats: TeamStats;
  history: TeamHistoryRow[];
}

export interface UnassociatedRow {
  liveTeamId: number;
  name: string;
  date: string;
  eventId: string;
  quizName: string;
  playerCount: number | null;
}

// All endpoints wrap their payload in { status, data }. These helpers unwrap.

export async function fetchTeams(venueId: number): Promise<TeamListResponse> {
  const res = await api.get(`/api/teams?venueId=${venueId}`);
  return res.data.data as TeamListResponse;
}

export async function fetchTeamDetail(id: number): Promise<TeamDetailResponse> {
  const res = await api.get(`/api/teams/${id}`);
  return res.data.data as TeamDetailResponse;
}

// With seasonId, only rows from game nights inside that season count.
export async function fetchUnassociated(
  venueId: number,
  seasonId?: number | null,
): Promise<UnassociatedRow[]> {
  const season = seasonId ? `&seasonId=${seasonId}` : '';
  const res = await api.get(`/api/teams/unassociated?venueId=${venueId}${season}`);
  return (res.data.data?.teams ?? []) as UnassociatedRow[];
}

export async function createTeam(name: string, venueId: number): Promise<TeamCore> {
  const res = await api.post('/api/teams', { name, venueId });
  return res.data.data.team as TeamCore;
}

export async function patchTeam(
  id: number,
  patch: { name?: string; notes?: string },
): Promise<void> {
  await api.patch(`/api/teams/${id}`, patch);
}

export async function assignToTeam(id: number, liveTeamIds: number[]): Promise<void> {
  await api.post(`/api/teams/${id}/assign`, { liveTeamIds });
}

export async function unassignFromTeam(id: number, liveTeamIds: number[]): Promise<void> {
  await api.post(`/api/teams/${id}/unassign`, { liveTeamIds });
}

export async function mergeTeam(id: number, intoId: number): Promise<void> {
  await api.post(`/api/teams/${id}/merge`, { intoId });
}

export async function deleteTeam(id: number): Promise<void> {
  await api.delete(`/api/teams/${id}`);
}

export async function fetchVenueOptions(): Promise<VenueOption[]> {
  const res = await api.get('/venues');
  return (res.data ?? []) as VenueOption[];
}

// Formatting helpers shared across the Teams screens.
export const fmtDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

export const fmtNum = (n: number | null | undefined, digits = 1): string =>
  n === null || n === undefined ? '—' : Number(n).toFixed(digits);
